import { useCallback, useState } from 'react'

import { RequestStatus } from 'core/domain/Request'

import type { NewShipmentCharge, ShipmentCharge } from '../../domain/ShipmentCharges'
import { createShipmentCharge } from '../../use-cases/createShipmentCharge'
import { uploadChargeAttachment } from '../../use-cases/uploadChargeAttachment'

export function useCreateShipmentCharge({
  shipmentUUID,
  onSuccess,
  onError,
  onChargeCreated,
  onChargeUpdated,
}: {
  shipmentUUID: string
  onSuccess: (charge: ShipmentCharge) => void
  onError: (error: string) => void
  onChargeCreated: (createdCharge: ShipmentCharge) => void
  onChargeUpdated: (updatedCharge: ShipmentCharge) => void
}) {
  const [requestStatus, setRequestStatus] = useState(RequestStatus.Initial)
  const [createdCharge, setCreatedCharge] = useState<ShipmentCharge>()

  const uploadAttachment = useCallback(
    async ({ charge, file }: { charge: ShipmentCharge; file: File }) => {
      setRequestStatus(RequestStatus.Pending)
      try {
        const uploadedAttachment = await uploadChargeAttachment({ chargeUUID: charge.uuid, file })
        const updatedCharge: ShipmentCharge = {
          ...charge,
          attachment: {
            fileName: file.name,
            fileUrl: uploadedAttachment.file,
          },
        }

        setRequestStatus(RequestStatus.Done)
        onChargeUpdated(updatedCharge)
        onSuccess(updatedCharge)
      } catch (errorMessage) {
        setRequestStatus(RequestStatus.Error)
        onError(errorMessage as string)
      }
    },
    [onSuccess, onError, onChargeUpdated]
  )

  const createCharge = useCallback(
    async (newCharge: NewShipmentCharge) => {
      setRequestStatus(RequestStatus.Pending)
      try {
        const charge = await createShipmentCharge({ shipmentUUID, newCharge })

        setRequestStatus(RequestStatus.Done)
        setCreatedCharge(charge)
        onChargeCreated(charge)
        if (newCharge.attachmentFile) {
          return uploadAttachment({ charge, file: newCharge.attachmentFile })
        } else {
          onSuccess(charge)
        }
      } catch (errorMessage) {
        setRequestStatus(RequestStatus.Error)
        onError(errorMessage as string)
      }
    },
    [shipmentUUID, onSuccess, onError, onChargeCreated, uploadAttachment]
  )

  const onCreateCharge = useCallback(
    (newCharge: NewShipmentCharge) => {
      // If the charge was already created, we should only retry the attachment upload
      if (!createdCharge) {
        createCharge(newCharge)
      } else if (newCharge.attachmentFile) {
        uploadAttachment({ charge: createdCharge, file: newCharge.attachmentFile })
      }
    },
    [createdCharge, createCharge, uploadAttachment]
  )

  return {
    onCreateCharge,
    isSubmitting: requestStatus === RequestStatus.Pending,
    chargeCreated: Boolean(createdCharge),
  }
}
